import React, { useEffect, useState } from 'react';
import { Box, Button, Grid, Typography } from '@mui/material';
import { useSelector } from 'react-redux';
import { nanoid, unwrapResult } from '@reduxjs/toolkit';

import NotificationCard from './NotificationCard';
import {
  allNotifications,
  markAllNotificationsHidden,
  markAllNotificationsRead,
  thunkMarkNotificationsAllRead,
  thunkMarkNotificationsHideAll,
} from '../../store/slices/notificationSlice';
import { Notification } from '../../types/interface';
import useTranslate from '../../hooks/useTranslate';
import {
  ModalNotificationDropDown,
  NotificationCardContainer,
  NotificationContainer,
  NotificationGrid,
} from '../../styled-components/NotificationModalStyledComponents';
import useDetectResize from '../../hooks/useDetectResize';
import { useAppDispatch } from '../../store/store';
import { fetchMyId } from '../../store/slices/individualSlice';

const NotificationComponent = (props: {
  closeModal: (value: React.SetStateAction<boolean>) => void;
}) => {
  const notifications: Notification[] = useSelector(allNotifications);
  const [notificationCount, setNotificationCount] = useState<number>(0);
  const [visibleCount, setVisibleCount] = useState<number>(0);
  const myId = useSelector(fetchMyId);
  const { isMobile, isTablet } = useDetectResize();
  const dispatch = useAppDispatch();
  const t = useTranslate();

  useEffect(() => {
    let readCount = notifications.filter((notification) => !notification.isRead).length;
    let visibleCount = notifications.filter((notification) => notification.isVisible).length;
    setNotificationCount(readCount);
    setVisibleCount(visibleCount);
  }, [notifications]);

  const markAllRead = () => {
    if (notificationCount > 0) {
      dispatch(thunkMarkNotificationsAllRead(myId!))
        .then(unwrapResult)
        .then((status: number) => {
          status === 200
            ? dispatch(markAllNotificationsRead(null))
            : alert(`${t('notification-errorCheckInternet')}`);
        });
    }
  };

  const hideAll = () => {
    if (visibleCount > 0) {
      dispatch(thunkMarkNotificationsHideAll(myId!))
        .then(unwrapResult)
        .then((status: number) => {
          status === 200
            ? dispatch(markAllNotificationsHidden(null))
            : alert(`${t('notification-errorCheckInternet')}`);
        });
    }
  };

  return (
    <ModalNotificationDropDown>
      <NotificationGrid
        container
        sx={{
          width: isMobile || isTablet ? '96vw' : 'min-content',
          padding: '5px',
        }}
      >
        <Grid sx={{ width: '100%' }}>
          <Box
            className="notif-dropdown-header"
            sx={{
              minHeight: '100px',
            }}
          >
            <Typography
              className="notif-dropdown-title"
              variant="h2"
              textTransform={'uppercase'}
              color={'#050505'}
              sx={{
                padding: '20px 15px',
                font: 'LexendDeca-SemiBold',
                fontSize: '16px',
              }}
            >
              {t('notificationTitle')}
            </Typography>
            <Box
              className="notif-panel"
              sx={{
                width: '100%',
                display: 'inline-flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '0px 15px',
              }}
            >
              <Typography variant="body2" className="notif-dropdown-unreadCount">
                {t('notificationCountTextI')}{' '}
                <span className="notif-count">{notificationCount}</span>{' '}
                {t('notificationCountTextII')}{' '}
              </Typography>
              <Button
                className="notif-dropdown-markAllAsRead"
                variant="text"
                onClick={(e) => {
                  hideAll();
                  e.stopPropagation();
                }}
              >
                {t('filterKeyEventsBtn')}
              </Button>
            </Box>
          </Box>
          <Box
            className="dropdown-line"
            sx={{
              width: '95%',
              borderBottom: '2px solid lightgray',
              marginLeft: 'auto',
              marginRight: 'auto',
            }}
          />
        </Grid>

        <Grid sx={{ width: '100%' }}>
          <Typography
            className="notif-dropdown-earlier"
            variant="h5"
            textTransform={'uppercase'}
            color={'#050505'}
            sx={{
              padding: '15px',
              color: 'black',
              fontSize: 'small',
            }}
          >
            {t('notificationText')}
          </Typography>
          <Box
            className="notif-dropdown-notifs hide-scroll"
            sx={{
              maxHeight: '285px',
              overflow: 'scroll',
              zIndex: '30',
            }}
          >
            {visibleCount > 0 ? (
              notifications.map((data: Notification) => {
                return (
                  data.alertUser !== -1 && (
                    <NotificationCard
                      key={nanoid()}
                      notification={data}
                      closeModal={props.closeModal}
                    />
                  )
                );
              })
            ) : (
              <NotificationCardContainer>
                <NotificationContainer>
                  <Typography className="notification-empty-message notif-noun" variant="body2">
                    {t('noNotifications')}
                  </Typography>
                </NotificationContainer>
              </NotificationCardContainer>
            )}
          </Box>
          <Box
            className="notif-dropdown-seeAll"
            sx={{
              float: 'right',
              fontSize: 'small',
              fontWeight: '600',
              cursor: 'pointer',
            }}
          >
            <Button
              className="notif-dropdown-markAllAsRead"
              variant="text"
              onClick={(e) => {
                markAllRead();
                e.stopPropagation();
              }}
              sx={{
                fontSize: '14px !important',
                fontWeight: '700 !important',
              }}
            >
              {t('notificationMarkAllBtn')}
            </Button>
          </Box>
        </Grid>
      </NotificationGrid>
    </ModalNotificationDropDown>
  );
};

export default NotificationComponent;
